import { useEffect, useState, type FormEvent } from 'react';
import type { BankAccount, BankAccountType, CreateBankAccountRequest } from '@stfors/shared-types';
import { Modal } from './Modal';
import { ApiError } from '../api/client';
import { useCreateBankAccount, useUpdateBankAccount } from '../api/queries/bank-accounts';
import './BankAccountFormModal.css';
import { Toggle } from './Toggle';

interface Props {
  open: boolean;
  /** When set, edit this account; otherwise create a new one. */
  initial?: BankAccount | null;
  onClose: () => void;
}

interface FormState {
  label: string;
  bank_name: string;
  account_type: BankAccountType;
  account_last4: string;
  routing_number: string;
  is_active: boolean;
  is_default: boolean;
  notes: string;
}

function blankForm(): FormState {
  return {
    label: '', bank_name: '', account_type: 'checking',
    account_last4: '', routing_number: '', is_active: true, is_default: false, notes: '',
  };
}

function formFromAccount(a: BankAccount): FormState {
  return {
    label: a.label,
    bank_name: a.bank_name ?? '',
    account_type: a.account_type,
    account_last4: a.account_last4 ?? '',
    routing_number: a.routing_number ?? '',
    is_active: a.is_active,
    is_default: a.is_default,
    notes: a.notes ?? '',
  };
}

export function BankAccountFormModal({ open, initial, onClose }: Props): JSX.Element {
  const isEdit = !!initial;
  const create = useCreateBankAccount();
  const update = useUpdateBankAccount();
  const [form, setForm] = useState<FormState>(blankForm);
  const [topError, setTopError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setForm(initial ? formFromAccount(initial) : blankForm());
      setTopError(null);
      setFieldErrors({});
    }
  }, [open, initial]);

  const set = <K extends keyof FormState>(key: K, val: FormState[K]) =>
    setForm((s) => ({ ...s, [key]: val }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTopError(null);
    setFieldErrors({});

    if (form.label.trim().length === 0) {
      setFieldErrors({ label: 'A name/label is required' });
      return;
    }
    const last4 = form.account_last4.replace(/\D+/g, '');
    if (form.account_last4.trim().length > 0 && last4.length !== 4) {
      setFieldErrors({ account_last4: 'Enter exactly the last 4 digits' });
      return;
    }
    const routing = form.routing_number.replace(/\D+/g, '');
    if (form.routing_number.trim().length > 0 && routing.length !== 9) {
      setFieldErrors({ routing_number: 'Routing number is 9 digits' });
      return;
    }

    const payload: CreateBankAccountRequest = {
      label: form.label.trim(),
      account_type: form.account_type,
      is_active: form.is_active,
      is_default: form.is_default,
      bank_name: form.bank_name.trim() || null,
      account_last4: last4.length === 4 ? last4 : null,
      routing_number: routing.length === 9 ? routing : null,
      notes: form.notes.trim() || null,
    };

    try {
      if (isEdit && initial) {
        await update.mutateAsync({ id: initial.id, ...payload });
      } else {
        await create.mutateAsync(payload);
      }
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        const body = err.body as { details?: { fieldErrors?: Record<string, string[]> } } | null;
        const fe = body?.details?.fieldErrors;
        if (fe) {
          const flat: Record<string, string> = {};
          for (const [k, v] of Object.entries(fe)) if (Array.isArray(v) && v.length > 0) flat[k] = v[0]!;
          setFieldErrors(flat);
          setTopError('Please fix the highlighted fields.');
          return;
        }
      }
      setTopError(`Could not ${isEdit ? 'save' : 'add'} the bank account. Please try again.`);
    }
  };

  const busy = create.isPending || update.isPending;

  return (
    <Modal
      title={isEdit ? `Edit Bank Account — ${initial?.label}` : 'Add Bank Account'}
      open={open}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn-charcoal-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="bank-account-form" className="btn-cyan-primary" disabled={busy}>
            {busy ? 'Saving…' : isEdit ? 'Save' : 'Add account'}
          </button>
        </>
      }
    >
      <form id="bank-account-form" className="bafm-form" onSubmit={onSubmit} noValidate>
        <label className="bafm-field">
          <span>Name / label *</span>
          <input
            type="text"
            className="form-input"
            value={form.label}
            onChange={(e) => set('label', e.target.value)}
            placeholder="Operating – Chase"
            maxLength={200}
            required
            autoFocus
          />
          {fieldErrors['label'] && <span className="field-error">{fieldErrors['label']}</span>}
        </label>

        <div className="bafm-row">
          <label className="bafm-field bafm-field-grow">
            <span>Bank name</span>
            <input
              type="text"
              className="form-input"
              value={form.bank_name}
              onChange={(e) => set('bank_name', e.target.value)}
              placeholder="Chase"
              maxLength={200}
            />
          </label>
          <label className="bafm-field">
            <span>Account type</span>
            <select
              className="form-input"
              value={form.account_type}
              onChange={(e) => set('account_type', e.target.value as BankAccountType)}
            >
              <option value="checking">Checking</option>
              <option value="savings">Savings</option>
              <option value="other">Other</option>
            </select>
          </label>
        </div>

        <div className="bafm-row">
          <label className="bafm-field">
            <span>Account (last 4)</span>
            <input
              type="text"
              className="form-input"
              value={form.account_last4}
              onChange={(e) => set('account_last4', e.target.value.replace(/\D+/g, '').slice(0, 4))}
              placeholder="1234"
              inputMode="numeric"
              maxLength={4}
            />
            {fieldErrors['account_last4'] && <span className="field-error">{fieldErrors['account_last4']}</span>}
          </label>
          <label className="bafm-field bafm-field-grow">
            <span>Routing number</span>
            <input
              type="text"
              className="form-input"
              value={form.routing_number}
              onChange={(e) => set('routing_number', e.target.value.replace(/\D+/g, '').slice(0, 9))}
              placeholder="(optional)"
              inputMode="numeric"
              maxLength={9}
            />
            {fieldErrors['routing_number'] && <span className="field-error">{fieldErrors['routing_number']}</span>}
          </label>
        </div>

        <div className="bafm-row bafm-checks">
          <Toggle
            checked={form.is_default}
            onChange={(v) => set('is_default', v)}
            label="Default account"
          />
          <Toggle
            checked={form.is_active}
            onChange={(v) => set('is_active', v)}
            label="Active"
          />
        </div>

        <label className="bafm-field">
          <span>Notes</span>
          <textarea
            className="form-input bafm-textarea"
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            rows={2}
            maxLength={10_000}
            placeholder="Deposit instructions, statement descriptor, etc."
          />
        </label>

        {topError && <p className="bafm-error-banner" role="alert">{topError}</p>}
      </form>
    </Modal>
  );
}
